import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import EventNoteIcon from '@mui/icons-material/EventNote';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface RulesContent {
  title: string;
  description: string;
  dressCode: string;
  conductRules: string;
  worshipGuidelines: string;
  photographyPolicy: string;
  specialObservances: string;
  safetyMeasures: string;
}

const defaultContent: RulesContent = {
  title: 'Rules & Guidelines',
  description: 'Important rules and guidelines for visitors',
  dressCode: 'Men should wear mundu (dhoti) and women should wear saree, churidhaar. Those wearing shirt, baniyan, lungi, and pants are not allowed to enter inside the temple wall. Footwear must be removed at the designated area before entering the inner courtyard.',
  conductRules: 'Maintain silence and reverence within the sanctum and surrounding corridors. Mobile phones must be switched to silent mode. Smoking, alcohol, and consumption of non-vegetarian food are strictly prohibited on temple grounds. Children must be accompanied by adults at all times. Littering is prohibited; please use the designated waste bins.',
  worshipGuidelines: 'Devotees should follow the queue system for darshan. Offerings (vazhipads) can be booked at the counter or online. During Abhishekam and special poojas, devotees are requested not to crowd the sanctum area. Prasadam distribution follows the completion of each pooja — please wait at the designated area. Personal poojas require prior appointment with the temple office.',
  photographyPolicy: 'Photography and videography are prohibited inside the main sanctum and sub-shrines. General photography is permitted in the outer courtyard and garden areas. During festivals, designated media zones are set up for press and approved videographers. Drone photography requires written permission from the temple administration. Social media posts should be respectful and not depict sacred rituals inappropriately.',
  specialObservances: 'During Navaratri, Mandala season, and Annual Utsavam, additional restrictions may apply including extended worship hours, special dress codes, and modified darshan routes. Ekadashi and Pradosham days have special timing schedules. Devotees performing Vrutham (fasting/penance) should register at the temple office for special access and facilities.',
  safetyMeasures: 'First aid facilities are available near the administration office. Fire extinguishers are placed at multiple locations. During festivals, police and fire services are stationed on the premises. Emergency exits are clearly marked. Devotees with medical conditions should inform the office for wheelchair/priority access. CCTV surveillance operates 24/7 for security.'
};

const timings = [
  { slot: 'Morning', time: '5:00 AM – 12:00 PM', detail: 'Nirmalyam, Usha Pooja, Ganapathy Homam, Navaka Pooja, Ucha Pooja', accent: '#f59e0b' },
  { slot: 'Afternoon', time: '12:00 PM – 4:30 PM', detail: 'Temple closed for mid-day rest', accent: '#64748b' },
  { slot: 'Evening', time: '4:30 PM – 8:00 PM', detail: 'Deeparadhana, Athazha Pooja, Harivarasanam', accent: '#8b5cf6' },
];

const ruleCards = [
  { icon: RecordVoiceOverIcon, title: 'Conduct Rules', content: 'conductRules' as const, accent: '#ef4444' },
  { icon: SelfImprovementIcon, title: 'Worship Guidelines', content: 'worshipGuidelines' as const, accent: '#22c55e' },
  { icon: EventNoteIcon, title: 'Special Observances', content: 'specialObservances' as const, accent: '#8b5cf6' },
  { icon: HealthAndSafetyIcon, title: 'Safety Measures', content: 'safetyMeasures' as const, accent: '#ec4899' },
];

export default function TempleRules() {
  const [content, setContent] = useState<RulesContent>(defaultContent);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, 'about_content', 'rules'), (docSnap) => {
      if (docSnap.exists()) {
        setContent(docSnap.data() as RulesContent);
      }
      setLoading(false);
    }, (error) => {
      console.error('Error loading rules:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Temple Rules — Kottayil Temple | ക്ഷേത്ര നിയമങ്ങൾ" description="Rules and guidelines for devotees visiting Kottayil Temple (Kotta-Kovilakam Kshethram). Dress code, visiting timings, and temple etiquette." path="/about/rules" keywords="Kottayil temple rules, visiting guidelines, dress code, temple timings, temple etiquette, ക്ഷേത്ര നിയമങ്ങൾ, കൊട്ടയിൽ ക്ഷേത്രം" />
      <PageBanner title={content.title} />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box sx={{ bgcolor: '#fff', borderRadius: '28px', border: '1px solid rgba(0,0,0,0.04)', boxShadow: '0 8px 40px rgba(0,0,0,0.04)', overflow: 'hidden', mb: 3.5 }}>
              <Box sx={{ background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2520 50%, #1a1a1a 100%)', px: { xs: 2.5, sm: 3.5 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden' }}>
                <Box sx={{ position: 'absolute', inset: 0, opacity: 0.04, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
                <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)`, backgroundSize: '200% 100%', animation: 'shimmer 3s infinite linear', '@keyframes shimmer': { '0%': { backgroundPosition: '-200% 0' }, '100%': { backgroundPosition: '200% 0' } } }} />
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%)' }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 2 }}>
                  <Box sx={{ width: 52, height: 52, borderRadius: '16px', background: 'linear-gradient(135deg, #22c55e, #4ade80)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(34,197,94,0.3)' }}>
                    <GavelIcon sx={{ fontSize: 26, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.3rem', sm: '1.5rem' }, color: '#fff', lineHeight: 1.2, mb: 0.3 }}>
                      {content.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)' }}>
                      {content.description}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 28, bgcolor: '#fff', borderRadius: '50% 50% 0 0 / 100% 100% 0 0' }} />
              </Box>
              <Box sx={{ px: { xs: 2.5, sm: 3.5 }, pt: 0.5, pb: 2.5 }}>
                <Typography sx={{ fontSize: '0.82rem', color: '#888', lineHeight: 1.8 }}>
                  Please observe these guidelines to ensure a peaceful and sacred experience for all devotees. Your cooperation helps maintain the sanctity of the temple.
                </Typography>
              </Box>
            </Box>

            {/* DARSHAN TIMINGS */}
            <Box sx={{ bgcolor: '#fff', borderRadius: '22px', p: { xs: 2.5, sm: 3 }, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', mb: 3.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <AccessTimeIcon sx={{ fontSize: 18, color: GOLD }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#222' }}>Darshan Timings</Typography>
              </Box>
              {timings.map((t, i) => (
                <Box key={t.slot} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5, borderBottom: i < timings.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                  <Box sx={{ px: 1.2, py: 0.5, borderRadius: '8px', bgcolor: `${t.accent}10`, minWidth: 72, textAlign: 'center', flexShrink: 0 }}>
                    <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: t.accent }}>{t.slot}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#333' }}>{t.time}</Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: '#aaa', mt: 0.3 }}>{t.detail}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* RULE SECTIONS */}
            {ruleCards.map((r) => {
              const Icon = r.icon;
              return (
                <Box key={r.title} sx={{ bgcolor: '#fff', borderRadius: '22px', p: { xs: 2.5, sm: 3 }, border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', mb: 3.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Icon sx={{ fontSize: 18, color: r.accent }} />
                    <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#222' }}>{r.title}</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.82rem', color: '#666', lineHeight: 1.9 }}>
                    {content[r.content]}
                  </Typography>
                </Box>
              );
            })}

            {/* PHOTOGRAPHY & VIDEOGRAPHY POLICY (COMBINED) */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '22px', overflow: 'hidden',
              border: '1.5px solid rgba(212,175,55,0.25)',
              boxShadow: '0 4px 24px rgba(212,175,55,0.08)',
              mb: 3.5,
            }}>
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ width: 5, background: `linear-gradient(180deg, ${GOLD}, #f59e0b)`, flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ height: 3, background: `linear-gradient(90deg, ${GOLD}, #e5c158, ${GOLD})` }} />
                  <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '14px',
                        background: `linear-gradient(135deg, ${GOLD}, #e5c158)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 6px 20px rgba(212,175,55,0.25)',
                      }}>
                        <CameraAltIcon sx={{ fontSize: 22, color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#222', lineHeight: 1.3 }}>
                          Photography &amp; Videography Policy
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#bbb', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Temple Guidelines &amp; Pricing
                        </Typography>
                      </Box>
                    </Box>

                    {/* Photography policy content from Firebase */}
                    <Typography sx={{ fontSize: '0.82rem', color: '#666', lineHeight: 1.9, mb: 2.5 }}>
                      {content.photographyPolicy}
                    </Typography>

                    {/* Videography pricing notice */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 },
                      bgcolor: 'rgba(46,125,50,0.04)', borderRadius: '14px',
                      border: '1px solid rgba(46,125,50,0.12)',
                      px: { xs: 2, sm: 2.5 }, py: { xs: 1.8, sm: 2 },
                    }}>
                      <Box sx={{
                        minWidth: 52, height: 52, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #2e7d32, #43a047)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 6px 20px rgba(46,125,50,0.25)',
                      }}>
                        <CurrencyRupeeIcon sx={{ fontSize: 26, color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '0.96rem' }, color: '#1a1a1a', lineHeight: 1.6 }}>
                          <Box component="span" sx={{ color: '#2e7d32', fontWeight: 900, fontSize: { xs: '1.15rem', sm: '1.25rem' } }}>{"\u20B9"}400</Box>
                          {' '}must be remitted in advance at the temple counter and obtain permission for videography.
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: '#777', mt: 0.5, lineHeight: 1.6 }}>
                          Kindly contact the temple office for prior approval before any photography or videography inside the temple premises.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* DRESS CODE & ENTRY RESTRICTIONS (COMBINED) */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '22px', overflow: 'hidden',
              border: '1.5px solid rgba(59,130,246,0.2)',
              boxShadow: '0 4px 24px rgba(59,130,246,0.06)',
              mb: 3.5,
            }}>
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ width: 5, background: 'linear-gradient(180deg, #3b82f6, #60a5fa)', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ height: 3, background: 'linear-gradient(90deg, #3b82f6, #60a5fa, #3b82f6)' }} />
                  <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '14px',
                        background: 'linear-gradient(135deg, #3b82f6, #60a5fa)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 6px 20px rgba(59,130,246,0.25)',
                      }}>
                        <CheckroomIcon sx={{ fontSize: 22, color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#222', lineHeight: 1.3 }}>
                          Dress Code &amp; Entry Restrictions
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#bbb', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Temple Norms
                        </Typography>
                      </Box>
                    </Box>

                    {/* Dress code content from Firebase */}
                    <Typography sx={{ fontSize: '0.82rem', color: '#666', lineHeight: 1.9, mb: 2.5 }}>
                      {content.dressCode}
                    </Typography>

                    {/* Pula/Valaima restriction notice */}
                    <Box sx={{
                      display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 },
                      bgcolor: 'rgba(239,68,68,0.04)', borderRadius: '14px',
                      border: '1px solid rgba(239,68,68,0.12)',
                      px: { xs: 2, sm: 2.5 }, py: { xs: 1.8, sm: 2 },
                    }}>
                      <Box sx={{
                        minWidth: 52, height: 52, borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 6px 20px rgba(239,68,68,0.25)',
                        flexShrink: 0,
                      }}>
                        <BlockIcon sx={{ fontSize: 26, color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '0.96rem' }, color: '#1a1a1a', lineHeight: 1.7 }}>
                          Those with <Box component="span" sx={{ fontWeight: 900, color: '#ef4444' }}>Pula</Box> and <Box component="span" sx={{ fontWeight: 900, color: '#ef4444' }}>Valaima</Box> are <Box component="span" sx={{ fontWeight: 900, color: '#ef4444' }}>not allowed</Box> to enter inside the temple wall.
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, color: '#777', mt: 0.5, lineHeight: 1.6 }}>
                          Please adhere to the traditional dress code and ritual purity norms before entering the temple premises.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* NOTICE */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '22px', overflow: 'hidden',
              border: '1.5px solid rgba(245,158,11,0.3)',
              boxShadow: '0 4px 24px rgba(245,158,11,0.08)',
              mb: 3.5,
            }}>
              <Box sx={{ display: 'flex' }}>
                <Box sx={{ width: 5, background: 'linear-gradient(180deg, #f59e0b, #fbbf24)', flexShrink: 0 }} />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ height: 3, background: 'linear-gradient(90deg, #f59e0b, #fbbf24, #f59e0b)' }} />
                  <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <Box sx={{
                        width: 44, height: 44, borderRadius: '14px',
                        background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 6px 20px rgba(245,158,11,0.25)',
                      }}>
                        <WarningAmberIcon sx={{ fontSize: 22, color: '#fff' }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' }, color: '#222', lineHeight: 1.3 }}>
                          Parking Information
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#bbb', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Gate &amp; Vehicle Guidelines
                        </Typography>
                      </Box>
                    </Box>
                    <Typography sx={{ fontSize: '0.82rem', color: '#666', lineHeight: 1.9 }}>
                      After 11:00 AM, once the temple entrance closes, the front gate will also be closed. All vehicles parked inside the gate must be moved to the parking area outside the gate before 11:00 AM. A designated parking ground is available outside the gate for your convenience.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* IMPORTANT REMINDER FOOTER */}
            <Box sx={{ background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2520 50%, #1a1a1a 100%)', borderRadius: '22px', p: { xs: 2.5, sm: 3 }, position: 'relative', overflow: 'hidden' }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '18px 18px' }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <CheckCircleOutlineIcon sx={{ fontSize: 18, color: GOLD }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: '#fff' }}>Remember</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.8 }}>
                  The temple is a place of worship and spiritual refuge. By following these guidelines, you contribute to the sacred atmosphere that has been cherished for over 150 years. For any queries or special requirements, please contact the temple administration office.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 2 }}>
                  {['Mon–Sun: 5 AM – 8 PM', 'Closed: 12 PM – 4:30 PM'].map((t) => (
                    <Box key={t} sx={{ px: 1.5, py: 0.5, borderRadius: '10px', bgcolor: 'rgba(212,175,55,0.12)' }}>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: GOLD }}>{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="about" activePath="/about/rules" />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
